import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "d-flex flex-column parent-container" }
const _hoisted_2 = { class: "nav-bar-layout-container" }
const _hoisted_3 = { class: "flex-grow-1 layout-container chat-container" }
const _hoisted_4 = { class: "d-flex flex-column" }
const _hoisted_5 = { class: "chat-header chat-content align-self-center" }
const _hoisted_6 = { class: "chat-sub-header" }
const _hoisted_7 = ["chat-index"]
const _hoisted_8 = {
  key: 0,
  class: "product-card-chat-container layout-container"
}

import { useRoute } from 'vue-router'
import { ref, nextTick, computed, onMounted, watch } from 'vue';
import { TalkToTheBookAIService } from '@/services/TalkToTheBookAIService';
import { AuthorizationService } from '@/services/AuthorizationService';
import { SharedResponseService } from '@/services/SharedResponseService';
import { SocialMediaShareService } from '@/services/SocialMediaShareService';
import { AiPointService } from '@/services/AiPointService';
import { TaazeProductUrlCreator } from '@/utils/taazeProductUrlCreator';
import { scrollToBottom, scrollTo } from '@/utils/scrollUtils';
import { v4 as uuidv4 } from 'uuid';

import { TAAZE_USER_PAGE_URL, TAAZE_T_POINT_HISTORY_PAGE_URL, OCOPY_WELCOME_MESSAGE, SHARE_RESPONSE_MESSAGE } from '@/assets/strings';
import { TAAZE_MAIN_COLOR } from '@/assets/colors';

import ProductCard from '@/components/ProductCard.vue';
import ChatInput from '@/components/ui/ChatInput.vue';
import TaazeNavbar from '@/components/layout/TaazeNavbar.vue';
import ChatMessage from '@/components/ChatMessage.vue'
import InfoModalComponent from '@/components/ui/InfoModalComponent.vue';
import AlertModalComponent from '@/components/ui/AlertModalComponent.vue';
import Chat from '@/interfaces/Chat';
import Product from '@/models/Product';
import ProductRepository from '@/repositories/ProductRepository';
import TalkToTheBookSource from '@/interfaces/TalkToTheBookSource';
import InsufficientPointsError from '@/errors/InsufficientPointsError';

const DISABLED_COLOR = '#E0E0E0';
const BASE_PROMPT = '請幫我總結這本書';
const TIMEOUT_ERROR_MODAL_TITLE = '系統忙碌中，請稍後再使用，謝謝。';
const INSUFFICIENT_FUNDS_MODAL_TITLE = '現有T點不足！ 每次提問需20T點。';

export default /*@__PURE__*/_defineComponent({
  __name: 'TalkToTheBookV1',
  setup(__props) {

const VERSION = process.env.VUE_APP_VERSION;

const productId: string | undefined = useRoute().query.product_id?.toString();
const userId: string | undefined = useRoute().query.user_id?.toString();
const deafultPrompt: string | undefined = (useRoute().query.prompt?.toString() ?? '').trim();
const firstQuestionIndex = Number(useRoute().query.q ?? 0);

const talkToTheBookAIService = new TalkToTheBookAIService(process.env.VUE_APP_TALK_TO_THE_BOOK_API_URL!);
const aiPointService = new AiPointService(process.env.VUE_APP_TALK_TO_THE_BOOK_API_URL!);
const authorizationService = new AuthorizationService(process.env.VUE_APP_TALK_TO_THE_BOOK_API_URL!);
const sharedResponseService = new SharedResponseService(process.env.VUE_APP_TALK_TO_THE_BOOK_API_URL!);
const socialMediaShareService = new SocialMediaShareService();
const taazeProductUrlCreator = new TaazeProductUrlCreator();
const productRepository = new ProductRepository(process.env.VUE_APP_TAAZE_API_URL!);

const chatHistory = ref<Chat[]>([]);
const userPoints = ref(0);
const infoModalComponent = ref<InstanceType<typeof InfoModalComponent> | null>(null);
const alertModalComponent = ref<InstanceType<typeof AlertModalComponent> | null>(null);
const chatInputValue = ref('');
const onPrompting = ref(false);
const isChatFocused = ref(false);
const isUserTyping = computed(() => chatInputValue.value !== '' || isChatFocused.value);
const chatBorderColor = computed(() => (chatInputValue.value !== '' && !isChatFocused.value) || onPrompting.value ? DISABLED_COLOR : TAAZE_MAIN_COLOR);
const chatIconColor = computed(() => onPrompting.value ? DISABLED_COLOR : TAAZE_MAIN_COLOR);
const chatPlaceholderColor = computed(() => onPrompting.value ? DISABLED_COLOR : TAAZE_MAIN_COLOR);

const welcomeMessage = computed(() => `${OCOPY_WELCOME_MESSAGE}\\
  您目前擁有的T點：共[{${TAAZE_MAIN_COLOR}}(${userPoints.value})](${TAAZE_T_POINT_HISTORY_PAGE_URL}#nounderline)點

  每次體驗AI閱讀服務只需 20 T點！

  如何獲得T點？[立即查看詳情](${TAAZE_USER_PAGE_URL})`);

let welcomeMessageChatId: string | null = null;

let sessionId = '';
let product: Product | null = null;
let aiPointReservationId = '';
let predefinedQuestions: { [key: string]: string } | null = null;
let promptCounter = 0;
let lastUserChatHeight = 0;
let streamBuffer = '';
let contentTimer: ReturnType<typeof setTimeout> | number;

const INSUFFICIENT_FUNDS_MODAL_BODY = computed(() => `您目前擁有的T點：共<span style="color: ${TAAZE_MAIN_COLOR}";>${userPoints.value}</span>點`);

onMounted(async () => {
  await setup();
});

watch(userPoints, () => {
  if (welcomeMessageChatId !== null) {
    const welcomeChat = chatHistory.value.find(chat => chat.id === welcomeMessageChatId);
    if (welcomeChat) {
      welcomeChat.content = welcomeMessage.value;
    }
  }
});

async function setup() {
  if (productId === undefined || userId === undefined) {
    return;
  }

  sessionId = await authorizationService.startSession(userId);
  product = await productRepository.getProductById(productId);
  predefinedQuestions = await talkToTheBookAIService.getPredefinedQuestions();
  userPoints.value = await aiPointService.getUserPoints(userId);

  const welcomeChat = addNewChat('bot', welcomeMessage.value, [], false, false);
  welcomeMessageChatId = welcomeChat.id;

  const firstQuestion = deafultPrompt ? deafultPrompt.trim() : predefinedQuestions[firstQuestionIndex] ?? BASE_PROMPT;

  await prompt(firstQuestion);

  window.addEventListener("beforeunload", () => {
    authorizationService.sessionEnd(sessionId, true);
  });
}

function onContentStreamed(content: string | null, promptSuggestions: string | null, sources: TalkToTheBookSource[]) {
  if (chatHistory.value.length === 2) {
    content = '';
  }

  const sliceAmount = (promptSuggestions !== null) ? 10 : 3;
  const timeout = 7;

  const lastBotChat = chatHistory.value.findLast(chat => chat.type === 'bot');

  if (lastBotChat) {
    lastBotChat.isLoading = false;

    // Append content to a buffer and start the timer if not already started
    streamBuffer += (content === null) ? '' : content;

    if (contentTimer) {
      clearTimeout(contentTimer);
    }

    // Consuming reservations here in the case that the user is in another tab. Which case the user would have to
    //  finish showing the response, which would delay the point consumption.
    if (promptSuggestions !== null && aiPointReservationId === '') {
      consumePointReservation();
    }

    // Function to output the content smoothly with timing control
    const outputContent = () => {
      if (streamBuffer.length > 0) {
        const charsToAdd = streamBuffer.slice(0, sliceAmount);
        lastBotChat.content += charsToAdd;
        streamBuffer = streamBuffer.slice(sliceAmount);
        contentTimer = setTimeout(outputContent, timeout);
      } else if (promptSuggestions !== null) {
        onStreamComplete(promptSuggestions, sources);
        return;
      }

      scrollToLastChat();
    };

    // Start the output process with a delay before adding the first character
    contentTimer = setTimeout(outputContent, timeout);
  }
}

function onStreamComplete(promptSuggestions: string, sources: TalkToTheBookSource[]) {
  if (userId === undefined) {
    return;
  }

  promptCounter++;

  sources.sort((a, b) => {
    return b.prod_title_main.length - a.prod_title_main.length;
  });

  if (chatHistory.value.length === 2) {
    chatHistory.value.pop();
  }

  const lastBotMessage = chatHistory.value[chatHistory.value.length - 1];
  lastBotMessage.suggestions = promptSuggestions.split('\n').filter(suggestion => suggestion.trim() !== '');
  lastBotMessage.isGenerating = false;

  onPrompting.value = false;

  if (aiPointReservationId !== '') {
    consumePointReservation();
  }

  scrollToLastChat();
}

async function prompt(prompt: string) {
  if (product === null || userId === undefined) {
    return;
  }

  try {
    onPrompting.value = true;

    addNewChat('user', prompt.replace(/^\d+\.\s/, ''), [], true);

    addNewChat();
    scrollToChatBottom(true);

    await aiPointService.reservePoints(userId).then((reservationId: string) => {
      aiPointReservationId = reservationId;
    });

    await talkToTheBookAIService.prompt(product.id, prompt, userId, sessionId, onContentStreamed);

  } catch (error: any) {
    if (error instanceof InsufficientPointsError) {
      infoModalComponent.value?.show();
    }

    if (error instanceof Error && error.name === 'AbortError') {
      alertModalComponent.value?.show();
    }

    removeLastChat();
    cancelPointReservation();
    onPrompting.value = false;
  }
}

async function consumePointReservation() {
  if (userId === undefined) {
    return;
  }

  if (aiPointReservationId !== '') {
    await aiPointService.consumePoints(userId, aiPointReservationId).then(() => {
      aiPointReservationId = '';
    }).catch((error) => {
      console.error('Error during point consumption');
    });
  }

  aiPointService.getUserPoints(userId).then((points: number) => {
    userPoints.value = points;
  }).catch((error) => {
    console.error('Error fetching user points');
  });
}

function cancelPointReservation() {
  if (userId === undefined) {
    return;
  }

  if (aiPointReservationId !== '') {
    aiPointService.cancelPoints(userId, aiPointReservationId).then(() => {
      aiPointReservationId = '';
    }).catch((error) => {
      console.error('Error during point cancellation');
    });
  }
}

function onMessageSend() {
  if (!onPrompting.value && chatInputValue.value !== '') {
    prompt(chatInputValue.value);
    resetChatInputState();
  }
}

function onPromptSuggestionSelected(suggestion: string) {
  if (!onPrompting.value) {
    prompt(suggestion);
  }
}

async function onChatShare(platform: 'facebook' | 'line', chat: Chat) {
  if (productId === undefined || userId === undefined) {
    return;
  }

  let previousChat: Chat | undefined = undefined;
  for (let i = 1; i < chatHistory.value.length; i++) {
    if (chatHistory.value[i].id === chat.id) {
      previousChat = chatHistory.value[i - 1];
      break;
    }
  }

  if (previousChat === undefined) {
    return;
  }

  const shareUrl = await sharedResponseService.createShareLink(productId, userId, previousChat.content, chat.content);
  const productUrl = taazeProductUrlCreator.createProductUrl(productId);

  const shareMessage = `${product?.name}，${SHARE_RESPONSE_MESSAGE}\n\n${shareUrl}`;

  if (platform === 'line') {
    const share_url = socialMediaShareService.getLineShareUrl(productUrl, shareMessage);
    window.open(share_url, '_blank');
  }

  if (platform === 'facebook') {
    const share_url = socialMediaShareService.getFacebookShareUrl(shareUrl, shareMessage);
    window.open(share_url, '_blank');
  }
}

function isPromptingEnabled() {
  return !onPrompting.value;
}

function isSharingEnabled(chatIndex: number, chat: Chat) {
  return (chatIndex > 0 && chat.type !== 'user' && chat.isLoading === false && chat.isGenerating === false);
}

function onChatFocused() {
  isChatFocused.value = true;

  const scrollableDiv = document.getElementsByClassName('chat-container')[0] as HTMLElement;
  const isAtBottom = scrollableDiv.scrollHeight - scrollableDiv.scrollTop <= scrollableDiv.clientHeight + 10;

  if (!isAtBottom && chatInputValue.value === '') {
    scrollTo(scrollableDiv, scrollableDiv.scrollTop - 130, 'instant', 0);
  }
}

function onChatOutOfFocus() {
  isChatFocused.value = false;

  const scrollableDiv = document.getElementsByClassName('chat-container')[0];
  const isAtBottom = scrollableDiv.scrollHeight - scrollableDiv.scrollTop <= scrollableDiv.clientHeight + 10;

  if (!isAtBottom && !isUserTyping.value) {
    nextTick(() => {
      scrollableDiv.scrollTop += 130;
    });

    return;
  }
}

function onModalButtonClick() {
  window.open(TAAZE_USER_PAGE_URL, '_blank');
}

function calculateLastUserChatHeight() {
  if (chatHistory.value.length === 0) {
    return 0;
  }

  nextTick(() => {
    lastUserChatHeight = 0;

    const lastUserChatIndex = chatHistory.value.findLastIndex(chat => chat.type === 'user');

    for (let index = 0; index < chatHistory.value.length; index++) {
      if (index > lastUserChatIndex) {
        break;
      }

      const chatElement = document.querySelector(`[chat-index="${index}"]`) as HTMLElement;
      lastUserChatHeight += chatElement.clientHeight;
    }
  });
}

function addNewChat(userType: 'user' | 'bot' = 'bot', content = '', suggestions = [], isLoading = true, isGenerating = true): Chat {
  const newChat: Chat = {
    id: uuidv4(),
    type: userType,
    content: content,
    suggestions: suggestions,
    isLoading: isLoading,
    isGenerating: isGenerating
  };
  
  chatHistory.value.push(newChat);

  if (userType === 'user') {
    calculateLastUserChatHeight();
  }
  
  return newChat;
}

function removeLastChat() {
  chatHistory.value.pop();
}

function scrollToChatBottom(forced = false) {
  const chatBox = document.getElementsByClassName('chat-container')[0] as HTMLElement;
  scrollToBottom(chatBox, forced);
}

function scrollToLastChat() {
  const chatBox = document.getElementsByClassName('chat-container')[0] as HTMLElement;
  const isAtBottom = chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;

  const lastUserChatIndex = chatHistory.value.findLastIndex(chat => chat.type === 'user');
  const chatElement = document.querySelector(`[chat-index="${lastUserChatIndex}"]`) as HTMLElement;
  const chatElementClientRect = chatElement.getBoundingClientRect();

  // Only scroll if the chat box is at the bottom and the last user chat is visible
  if (isAtBottom && chatElementClientRect.y > 0) {
    scrollTo(chatBox, lastUserChatHeight, 'smooth', 50);
  }
}

function resetChatInputState() {
  chatInputValue.value = '';
  isChatFocused.value = false;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(TaazeNavbar)
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _cache[1] || (_cache[1] = _createTextVNode("Talk to the Books ")),
            _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(VERSION)) + " 版 ", 1)
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chatHistory.value, (chat, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: index,
              "chat-index": index
            }, [
              _createVNode(ChatMessage, {
                chat: chat,
                "is-prompting-enabled": isPromptingEnabled(),
                "is-share-enabled": isSharingEnabled(index, chat),
                onOnPromptSuggestionSelected: onPromptSuggestionSelected,
                onOnShareClick: onChatShare
              }, null, 8, ["chat", "is-prompting-enabled", "is-share-enabled"])
            ], 8, _hoisted_7))
          }), 128))
        ])
      ]),
      (_unref(product) != null)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(ChatInput, {
              modelValue: chatInputValue.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((chatInputValue).value = $event)),
              readonly: false,
              "is-chat-enabled": true,
              "is-focused": isChatFocused.value,
              "border-color": chatBorderColor.value,
              "icon-color": chatIconColor.value,
              "placeholder-color": chatPlaceholderColor.value,
              onOnMessageSend: onMessageSend,
              onOnFocused: onChatFocused,
              onOnOutOfFocus: onChatOutOfFocus
            }, null, 8, ["modelValue", "is-focused", "border-color", "icon-color", "placeholder-color"]),
            _createVNode(_Transition, { name: "slide-fade" }, {
              default: _withCtx(() => [
                (!isUserTyping.value)
                  ? (_openBlock(), _createBlock(ProductCard, {
                      key: 0,
                      product: _unref(product)
                    }, null, 8, ["product"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(InfoModalComponent, {
      ref_key: "infoModalComponent",
      ref: infoModalComponent,
      header: INSUFFICIENT_FUNDS_MODAL_TITLE,
      body: INSUFFICIENT_FUNDS_MODAL_BODY.value,
      "button-text": "如何賺T點",
      onOnButtonClick: onModalButtonClick
    }, null, 8, ["body"]),
    _createVNode(AlertModalComponent, {
      ref_key: "alertModalComponent",
      ref: alertModalComponent,
      header: TIMEOUT_ERROR_MODAL_TITLE,
      "button-text": "關閉",
      onOnButtonClick: ($event: any) => (null)
    }, null, 512)
  ], 64))
}
}

})